/* -----------------Globals--------------- */
import { Image, SectionList } from "react-native";

import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------Helpers & Hooks--------------- */
import { getPremiumContext } from "@memorang/helpers/bundle";
import Button from "@memorang/ui/components/Button";
/* -----------------UI--------------- */
import { Divider, Text, TouchableRipple } from "react-native-paper";

/* -----------------Types--------------- */
import type { Category, ExamDetails, StateDetails } from "@memorang/types";

import useMultipleExams from "@features/exam/contexts/useMultipleExams";
import { getProductTitle } from "@features/exam/helpers";
/* -----------------Child components--------------- */
import ProductListItem from "@features/store/components/ProductListItem";
import { getShowStudyPackViews } from "@helpers/exams";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import type { Bundle, ProductType } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import EmptyState from "@memorang/ui/components/EmptyView";
import { useRouter } from "expo-router";
import { useState } from "react";
import { PurchaseDialog } from "../PurchaseDialog";

export type Section = {
	title: string;
	data: ExamDetails[];
	icon: string;
};

const getCustomProductTitle = ({
	state,
	category,
	productType,
}: { state: StateDetails; category: Category[]; productType: ProductType }) => {
	return `${state.name} ${category[0].name} ${state.alias} ${getProductTitle(productType)}`;
};
type Props = {
	products: Section[];
	handleSwitchExamContext: (context: ExamDetails) => void;
	handleClearSearch?: () => void;
	showProducts?: boolean;
	showChevronRight?: boolean;
	stickySectionHeadersEnabled?: boolean;
	searchQuery?: string;
};
const ProductsList = ({
	products,
	handleSwitchExamContext,
	handleClearSearch,
	showProducts = true,
	showChevronRight,
	searchQuery,
}: Props) => {
	const router = useRouter();
	const { currentExamContext } = useExamContext();

	const { relatedExamsIds, stateAlias } = useMultipleExams();

	const practiceTestEnabled = useAppStore(
		(state) => state.exam.practiceTest.enabled,
	);
	const theme = useAppTheme();
	const [selectedBundle, setSelectedBundle] = useState<Bundle | undefined>(
		undefined,
	);
	const renderSectionHeader = ({
		section: { title, icon },
	}: {
		section: Section;
	}) => {
		if (products.length === 1) {
			return <Box marginTop={16} />;
		}
		return (
			<Box padding={16} gap={16}>
				<Box flexDirection="row" alignItems="center" gap={1}>
					{icon && (
						<Image
							source={{
								uri: icon,
							}}
							style={{
								width: 32,
								height: 32,
								marginRight: 8,
							}}
						/>
					)}
					<Text variant="titleMedium">{title}</Text>
				</Box>
				<Divider />
			</Box>
		);
	};

	const renderItem = ({ item: examItem }: { item: ExamDetails }) => {
		const {
			exam: {
				name: subtitle,
				bundles: products,
				id,
				studyPackCollection,
				category,
			},
		} = examItem;

		const isCurrentExamContext = id === currentExamContext.examId;

		const isRelatedExam = relatedExamsIds?.includes(id);

		return (
			<Box paddingBottom={16}>
				<TouchableRipple
					onPress={async () => {
						await handleSwitchExamContext(examItem);
					}}
				>
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
						paddingHorizontal={16}
					>
						<Text
							style={{
								flex: 1,
							}}
							variant="titleSmall"
						>
							{subtitle}
						</Text>
						{showChevronRight ? (
							<Icon
								name="chevron-right"
								color={theme.colors.text}
								size={24}
								style={{
									paddingVertical: 8,
								}}
								onPress={async () => {
									await handleSwitchExamContext(examItem);
								}}
							/>
						) : (
							<>
								{isCurrentExamContext ? (
									<Button icon="check" disabled>
										Current Exam
									</Button>
								) : (
									<Button
										compact
										mode="text"
										onPress={async () => {
											await handleSwitchExamContext(examItem);
										}}
										icon="arrow-right-bottom"
									>
										Switch to Exam
									</Button>
								)}
							</>
						)}
					</Box>
				</TouchableRipple>
				{showProducts &&
					// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: we need to show all the products
					products.map((productItem) => {
						const { subscriptionExpired, subscriptionExpirationDate } =
							getPremiumContext(productItem.metadata);
						const expirationDateText = subscriptionExpirationDate
							? `Expires ${subscriptionExpirationDate}`
							: "";
						const bundleStatusText = subscriptionExpired
							? "Expired"
							: expirationDateText;

						const showStudyPackViews = getShowStudyPackViews(
							Number(studyPackCollection?.[0]),
						);

						if (
							isWeb &&
							productItem.bundle.bundleType === "STUDY_PACK" &&
							!showStudyPackViews
						) {
							return null;
						}
						if (
							!isWeb &&
							(productItem.bundle.bundleType === "PRACTICE_TEST" ||
								(productItem.bundle.bundleType === "EXAM" &&
									productItem.bundle.name.includes("Practice"))) &&
							!practiceTestEnabled.mobile
						) {
							return null;
						}

						const customTitle =
							isRelatedExam && stateAlias
								? getCustomProductTitle({
										state: stateAlias,
										category,
										productType: productItem.bundle.bundleType,
									})
								: undefined;

						return (
							<Box key={productItem.bundle.id}>
								<ProductListItem
									bundle={productItem.bundle}
									size={64}
									customTitle={customTitle}
									statusText={bundleStatusText}
									expired={subscriptionExpired as boolean}
									showRightView
									handleOpenProductModal={() => {
										if (isWeb) {
											setSelectedBundle(productItem.bundle);
										} else {
											router.push({
												pathname: "/purchase",
												params: {
													productType: productItem.bundle.bundleType,
													fromStore: "true",
													explicitProductNumericId:
														productItem.bundle.numericId,
													explicitProductId: productItem.bundle.id,
												},
											});
										}
									}}
								/>
							</Box>
						);
					})}
			</Box>
		);
	};

	if (!products.length) {
		return (
			<Box flexGrow={1} height={"100%"}>
				<EmptyState
					title={`No exams found for search term "${searchQuery}"`}
					subtitle="Try a different search term"
					cta="Clear Search"
					handleAction={handleClearSearch}
				/>
			</Box>
		);
	}

	const showPurchaseDialog = selectedBundle != null;

	return (
		<>
			<SectionList
				sections={products}
				contentContainerStyle={{
					flexGrow: 1,
					paddingBottom: 16,
				}}
				showsVerticalScrollIndicator={false}
				keyExtractor={(item) => item.id}
				renderItem={renderItem}
				stickySectionHeadersEnabled={true}
				renderSectionHeader={renderSectionHeader}
			/>
			{showPurchaseDialog ? (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={() => setSelectedBundle(undefined)}
					productType={selectedBundle?.bundleType}
					explicitProductId={selectedBundle?.id}
					explicitProductNumericId={selectedBundle?.numericId}
				/>
			) : null}
		</>
	);
};

export { ProductsList };
